import { createBrowserRouter } from 'react-router-dom'
import { RequireAuth } from '@/auth/RequireAuth'
import { RequirePermission } from '@/auth/RequirePermission'
import { buildLoginPath, LOGIN_PATH } from '@/auth/redirect'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary'
import { setUnauthorisedHandler } from '@/lib/api'

/**
 * Screens are code-split: the login page is the only chunk an unauthenticated visitor
 * ever downloads, and a warehouse user never pays for the product editor.
 *
 * `lazy` returns the route's Component, so the page modules keep named exports
 * (house style) instead of defaults.
 */
export const router = createBrowserRouter([
  {
    // Pathless root: renders its Outlet, and owns the error boundary for everything.
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: LOGIN_PATH,
        lazy: async () => ({ Component: (await import('@/pages/LoginPage')).LoginPage }),
      },
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              {
                index: true,
                lazy: async () => ({
                  Component: (await import('@/pages/DashboardPage')).DashboardPage,
                }),
              },
              {
                path: 'products',
                lazy: async () => ({
                  Component: (await import('@/pages/products/ProductListPage')).ProductListPage,
                }),
              },
              {
                  // Mirrors the backend's catalogue:write permission, so staff without it never
                  // open a form that cannot be submitted.
                element: (
                    <RequirePermission permission="catalogue:write" action="Creating and editing products" />
                ),
                children: [
                  {
                    path: 'products/new',
                    lazy: async () => ({
                      Component: (await import('@/pages/products/ProductCreatePage'))
                        .ProductCreatePage,
                    }),
                  },
                  {
                    path: 'products/:id',
                    lazy: async () => ({
                      Component: (await import('@/pages/products/ProductEditPage')).ProductEditPage,
                    }),
                  },
                ],
              },
              {
                path: 'orders',
                lazy: async () => ({
                  Component: (await import('@/pages/orders/OrderListPage')).OrderListPage,
                }),
              },
              {
                path: 'orders/:id',
                lazy: async () => ({
                  Component: (await import('@/pages/orders/OrderDetailPage')).OrderDetailPage,
                }),
              },
              {
                // Its own route rather than a modal: staff print it, reload it and
                // keep it open on a second tab while packing.
                path: 'orders/:id/packing-slip',
                lazy: async () => ({
                  Component: (await import('@/pages/orders/PackingSlipPage')).PackingSlipPage,
                }),
              },
              {
                path: 'inventory',
                lazy: async () => ({
                  Component: (await import('@/pages/inventory/InventoryPage')).InventoryPage,
                }),
              },
              {
                path: 'activity',
                lazy: async () => ({
                  Component: (await import('@/pages/activity/ActivityPage')).ActivityPage,
                }),
              },
              {
                path: 'notifications',
                lazy: async () => ({
                  Component: (await import('@/pages/notifications/NotificationListPage'))
                    .NotificationListPage,
                }),
              },
              {
                // Reporting is Administration-only, mirroring the backend policy on
                // GET /admin/reports/*. Gating the route means a warehouse user meets
                // an explanation rather than a 403 from the API.
                element: <RequirePermission permission="report:read" action="Reporting" />,
                children: [
                  {
                    path: 'reports',
                    lazy: async () => ({
                      Component: (await import('@/pages/reports/OrdersReportPage'))
                        .OrdersReportPage,
                    }),
                  },
                ],
              },
              {
                element: <RequirePermission permission="shipment:read" action="Shipments" />,
                children: [
                  {
                    path: 'shipments',
                    lazy: async () => ({
                      Component: (await import('@/pages/shipments/ShipmentsPage')).ShipmentsPage,
                    }),
                  },
                ],
              },
              {
                element: <RequirePermission permission="customer:read" action="Customers" />,
                children: [
                  {
                    path: 'customers',
                    lazy: async () => ({
                      Component: (await import('@/pages/customers/CustomersPage')).CustomersPage,
                    }),
                  },
                ],
              },
              {
                // staff:manage is the permission that can grant permissions, so this gate is the
                // one most worth mirroring exactly.
                element: <RequirePermission permission="staff:manage" action="Staff and roles" />,
                children: [
                  {
                    path: 'staff',
                    lazy: async () => ({
                      Component: (await import('@/pages/staff/StaffPage')).StaffPage,
                    }),
                  },
                ],
              },
              {
                element: <RequirePermission permission="settings:manage" action="Settings" />,
                children: [
                  {
                    path: 'settings',
                    lazy: async () => ({
                      Component: (await import('@/pages/settings/SettingsPage')).SettingsPage,
                    }),
                  },
                ],
              },
              {
                path: 'categories',
                lazy: async () => ({
                  Component: (await import('@/pages/categories/CategoryListPage')).CategoryListPage,
                }),
              },
              {
                path: 'collections',
                lazy: async () => ({
                  Component: (await import('@/pages/collections/CollectionListPage'))
                    .CollectionListPage,
                }),
              },
              {
                path: '*',
                lazy: async () => ({
                  Component: (await import('@/pages/NotFoundPage')).NotFoundPage,
                }),
              },
            ],
          },
        ],
      },
    ],
  },
])

/**
 * When the api client gives up on a session it needs to send the user to /login.
 * Handing it the router turns that into a client-side navigation (state preserved,
 * no white flash) instead of the `window.location.assign` fallback.
 */
setUnauthorisedHandler(() => {
  const { pathname, search, hash } = window.location
  if (pathname === LOGIN_PATH) return
  void router.navigate(buildLoginPath({ pathname, search, hash }), { replace: true })
})
